/**
 * Componente de barra de filtros para Tracking
 * Filters bar component for Tracking
 */

import { createHttpClient } from '@adapters/HttpClient';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { apiClientWH } from '@shared/services';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { TrackingFilters } from '../../models/TrackingFilters';

interface TrackingFiltersBarProps {
  filters?: TrackingFilters;
  onFiltersChange: (filters: TrackingFilters) => void;
  section?: 'dashboard' | 'api_calls' | 'history' | 'unmatched';
}

interface CourierOption {
  id: number;
  name: string;
}

export const TrackingFiltersBar = ({
  filters,
  onFiltersChange,
  section = 'dashboard',
}: TrackingFiltersBarProps) => {
  const { t } = useTranslation('trackingModule');
  const httpClient = createHttpClient(apiClientWH);

  // Cargar couriers
  const { data: couriersData = [] } = useQuery<CourierOption[]>({
    queryKey: ['tracking-couriers'],
    queryFn: async () => {
      try {
        const raw = await httpClient.get<any>('/couriers');
        const list = Array.isArray(raw) ? raw : raw.data ?? [];
        return list.map((courier: any) => ({
          id: parseInt(String(courier.id), 10),
          name: courier.name,
        }));
      } catch (error) {
        console.error('Error loading couriers:', error);
        return [];
      }
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Helper para formatear fecha a string YYYY-MM-DD
  const formatDateToString = (date: Date | undefined): string => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper para parsear string a Date
  const parseStringToDate = (dateString: string | undefined): Date | undefined => {
    if (!dateString) return undefined;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? undefined : date;
  };

  // Aplicar filtros automáticamente
  const applyFilters = useCallback((updates: Partial<TrackingFilters>) => {
    const newFilters: TrackingFilters = {
      ...filters,
      ...updates,
    };
    onFiltersChange(newFilters);
  }, [filters, onFiltersChange]);

  // Manejar cambio de courier
  const handleCourierChange = useCallback((value: string) => {
    const courierId = value && value !== '' ? parseInt(value, 10) : null;
    applyFilters({ courier_id: courierId });
  }, [applyFilters]);

  // Manejar cambio de horas
  const handleHoursChange = useCallback((value: string) => {
    const hoursNum = parseInt(value, 10);
    if (hoursNum > 0 && hoursNum <= 48) {
      applyFilters({ hours: hoursNum });
    }
  }, [applyFilters]);

  // Manejar cambio de rango de fechas
  const handleDateRangeUpdate = useCallback((values: { range: DateRange }) => {
    const { range } = values;
    applyFilters({
      date_from: formatDateToString(range.from),
      date_to: formatDateToString(range.to),
    });
  }, [applyFilters]);

  const hoursOptions = [
    { label: '12 horas', value: '12' },
    { label: '24 horas', value: '24' },
    { label: '48 horas', value: '48' },
  ];

  // Obtener valores actuales para los controles
  const currentCourierId = filters?.courier_id !== null && filters?.courier_id !== undefined
    ? filters.courier_id.toString()
    : '';
  const currentHours = filters?.hours?.toString() || '24';
  const currentDateFrom = parseStringToDate(filters?.date_from);
  const currentDateTo = parseStringToDate(filters?.date_to);

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3 flex-wrap">
      {/* Courier Filter */}
      {section !== 'api_calls' && (
        <div className="min-w-[180px]">
          <label className="block text-xs text-muted-foreground mb-1.5">
            {t('filters.courier')}
          </label>
          <Combobox
            options={[
              { label: t('filters.all'), value: '' },
              ...couriersData.map((courier) => ({
                label: courier.name,
                value: courier.id.toString(),
              })),
            ]}
            value={currentCourierId}
            setValue={handleCourierChange}
            placeholder={t('filters.select_courier')}
          />
        </div>
      )}

      {/* Hours Filter (solo para dashboard) */}
      {section === 'dashboard' && (
        <div className="min-w-[150px]">
          <label className="block text-xs text-muted-foreground mb-1.5">
            {t('filters.hours')}
          </label>
          <Combobox
            options={hoursOptions}
            value={currentHours}
            setValue={handleHoursChange}
            placeholder={t('filters.hours')}
          />
        </div>
      )}

      {/* Date Range Picker (solo para unmatched) */}
      {section === 'unmatched' && (
        <div className="flex-1 min-w-[300px]">
          <label className="block text-xs text-muted-foreground mb-1.5">
            {t('filters.date_range')}
          </label>
          <DateRangePicker
            initialDateFrom={currentDateFrom}
            initialDateTo={currentDateTo}
            onUpdate={handleDateRangeUpdate}
            align="start"
            showPresets={true}
            triggerClassName="bg-card-bg border-card-border w-full"
            placeholder={t('filters.select_date_range', 'Seleccionar rango')}
            dataTestId="tracking-date-range-picker"
          />
        </div>
      )}
    </div>
  );
};

